import EmailIcon from '@mui/icons-material/Email'
import FacebookIcon from '@mui/icons-material/Facebook'
import InstagramIcon from '@mui/icons-material/Instagram'
import LocalPhoneIcon from '@mui/icons-material/LocalPhone'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { Box, TableCell, Typography, styled } from '@mui/material'
import { Stack } from '@mui/system'
import { useEffect } from 'react'
import { useBranch } from '../../context/branch_context'
import { ClientDocumentType } from '../../context/client_context'
import { ContactTypeEnum, useConfigs } from '../../context/configs_context'
import { ParcelInfo, PreSaleInfo, SaleContract, SaleInfo } from '../../context/sale_context'
import {
    getDateInDDMMYYYYFormat,
    getDateString,
    maskCurrency,
    numeroPorExtensoEspanhol,
} from '../../utils/maks'

const StyledTableCell = styled(TableCell)(({}) => ({
    color: 'text.primary',
    border: 'none',
    padding: '1px 2px',
    fontSize: '11px',
}))

interface SaleContractNoteProps {
    contract?: SaleContract
    sale?: SaleInfo
    parcel?: ParcelInfo
    budget?: PreSaleInfo
    maxHeight?: boolean
}

const ContactIcon: Record<string, JSX.Element> = {
    Facebook: <FacebookIcon />,
    Instagram: <InstagramIcon />,
    Whatsapp: <WhatsAppIcon />,
    Endereço: <LocationOnIcon />,
    Telefone: <LocalPhoneIcon />,
    Email: <EmailIcon />,
}

export function SaleContractNote({ contract, sale, parcel, budget, maxHeight }: SaleContractNoteProps) {
    const { configs, contactTypes, base64Logo } = useConfigs()
    const { branches } = useBranch()

    const endDate =
        budget?.date ?? parcel?.endDate ?? sale?.expDate ?? (contract?.expDate as string)
    const total = budget?.total ?? parcel?.total ?? sale?.total ?? (contract?.value as number)
    const data = budget ?? sale ?? contract

    const contacts = data?.branch ? data.branch.contacts : configs.contacts
    const companyName =
        data?.branch && configs.showBranchNameInNote
            ? data?.branch.name
            : configs.companyName
    const companyLogo = data?.branch?.companyLogo
        ? branches.find(b => b.id === data?.branch?.id)?.companyLogo?.content
        : base64Logo
    const companySlogan = data?.branch?.companySlogan
        ? data?.branch.companySlogan
        : configs.companySlogan

    return (
        <Stack
            direction="column"
            width="100%"
            px="20px"
            py="10px"
            height={maxHeight ? "100vh" : "50vh"}
        >
            <Stack direction={'row'} width={'100%'}>
                <Stack
                    direction={'column'}
                    border={'1px solid black'}
                    borderRight={'0'}
                    borderRadius={'12px'}
                    width={'70%'}
                >
                    <Stack direction={'row'} p={'10px'}>
                        <Box
                            component={'img'}
                            src={companyLogo}
                            width={'220px'}
                            height={'110px'}
                            zIndex={'-1'}
                        />
                        <Stack textAlign={'center'} flex={'1'} px={'15px'}>
                            <Typography
                                fontSize={'14px'}
                                sx={{ wordBreak: 'break-word' }}
                            >
                                {companyName}
                            </Typography>
                            <Typography
                                fontSize={'9px'}
                                sx={{ wordBreak: 'break-word' }}
                                fontStyle={'italic'}
                                whiteSpace={'pre-line'}
                            >
                                {`${companySlogan}`}
                            </Typography>
                            <Typography
                                fontSize={'8px'}
                                sx={{ wordBreak: 'break-word' }}
                            >
                                {contactTypes
                                    .filter((ct) =>
                                        contacts.find(
                                            (c) => c.contactType.id === ct.id
                                        )
                                    )
                                    .map(
                                        (ct) =>
                                            `${ct.name}: ${contacts
                                                .filter(
                                                    (c) =>
                                                        c.contactType.id ===
                                                        ct.id
                                                )
                                                .map((c) => c.value)
                                                .join(', ')}`
                                    )
                                    .join('; ')}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
                <Stack
                    direction={'column'}
                    border={'1px solid black'}
                    borderRadius={'12px'}
                    width={'30%'}
                >
                    <Stack direction={'row'} p={'10px'} textAlign={'center'}>
                        <Box flex={'1'}>
                            <Typography fontSize={'12px'}>
                                PAGARE A LA ORDEN
                            </Typography>
                            <Typography fontSize={'12px'} mt={'3px'}>
                                Nro.: {sale?.bill ?? '---'}
                            </Typography>
                            {parcel && (
                                <Typography fontSize={'12px'} mt={'3px'}>
                                    Cuota {parcel.parcelNumber}/
                                    {sale?.totalParcels}
                                </Typography>
                            )}
                            {
                                <Typography fontSize={'12px'} mt={'3px'}>
                                    Vencimiento:{' '}
                                    {getDateInDDMMYYYYFormat(
                                        new Date(`${endDate} 00:00:00`)
                                    )}
                                </Typography>
                            }
                            {
                                <Typography
                                    fontSize={'22px'}
                                    mt={'3px'}
                                    fontWeight={'bold'}
                                >
                                    {maskCurrency(total, data?.coin)}
                                </Typography>
                            }
                        </Box>
                        {/* <Typography
                            sx={{
                                writingMode: 'vertical-rl',
                                textOrientation: 'mixed',
                            }}
                            p={'30px'}
                            color={'error'}
                            fontSize={'26px'}
                        >
                            {sale.bill}
                        </Typography> */}
                    </Stack>
                </Stack>
            </Stack>
            <Stack
                width={'100%'}
                border={'1px solid black'}
                borderRadius={'12px'}
                direction={'column'}
                padding={'10px'}
                mt={'10px'}
            >
                <Stack direction={'row'} alignItems={'center'}>
                    <Box width={'70%'}>
                        <Typography fontSize={'16px'} fontWeight={'bold'}>
                            Nombre: {data?.client?.name || '---'}
                        </Typography>
                        <Typography fontSize={'16px'} fontWeight={'bold'}>
                            {data?.client?.documentType ===
                            ClientDocumentType.RUC
                                ? 'Ruc'
                                : 'Documento'}
                            : {data?.client?.document || '---'}
                        </Typography>
                    </Box>
                    <Box>
                        <Box>
                            <Typography fontSize={'11px'}>
                                Fecha:{' '}
                                {getDateString({
                                    date: data?.date,
                                    hour: data?.hour,
                                    configs: configs,
                                    isSummerTime: !!data?.isSummerTime,
                                })}
                            </Typography>
                            <Typography fontSize={'11px'}>
                                Telefonos:{' '}
                                {data?.client?.phoneNumbers.join(', ') || '---'}
                            </Typography>
                            <Typography fontSize={'11px'}>
                                Dirección: {data?.client?.address || '---'}
                            </Typography>
                        </Box>
                    </Box>
                </Stack>
            </Stack>
            <Typography fontSize={'11px'} mt={'10px'}>
                El dia {getDateInDDMMYYYYFormat(new Date(endDate as string))},
                Pageré(mos) solidariamente en la ciudad de{' '}
                {
                    contacts.find(
                        (c) => c.contactType.id === ContactTypeEnum.City
                    )?.value
                }{' '}
                a {companyName} o a su ordem la cantidad de (
                {numeroPorExtensoEspanhol(total, data?.coin ?? undefined)}
            </Typography>
            <Typography fontSize={'9.5px'}>
                Sin protesto por igual valor recebido en EFECTIVO Y/O
                MERCADERIAS a mi entera satisfacion. Si por cualquier
                circustancia no fuere abonado este pagare a su vencimiento,
                devengara en interes de {data?.client?.fees}% diario por el
                tiempo de la mora. El simple vencimiento estabelecera la mora.
                Asi mismo habrá una comisión de cobranza de 3% debido a la falta
                de pago en la fecha respectiva sobre el documento convenido y me
                obligo a pagar cualquier gasto en que incurra al Acreedor con
                relación a estre prestamo en caso de que el mismo sea reclamado
                por via judicial. La falta de pago de un pagare a su
                vencimiento, producira la caducidad de los demas plazos no
                fenecidos por el solo transcurso del tiempo, sin necesidad de
                delligencia de cobro ni de protesto haciendo exigible la
                totalidad del credito, y por conseguiente a los efectos de la
                reclamacion judicial autorizo desde ya la Acreedor a ejecutar
                los pagares siguintes correspondiente a esta operacion que
                importe el salario completo de la aludida deuda y la inclusion a
                la Base de Datos de Inforcomf, conforme a lo estabelecido en la
                ley 6534/20, como tambien para que se pueda proveer información
                a terceros interesados. Las notificaciones hechas en el
                domicilio fijado tendra el valor de aquellas efectuadas
                personalmente, salvo aviso por escrito de cambio notificado al
                acreedor, queda convenida la jurisdicción de los tribunales de{' '}
                {
                    contacts.find(
                        (c) => c.contactType.id === ContactTypeEnum.City
                    )?.value
                }
            </Typography>
            <Stack width={'100%'} direction={'row'} alignItems={'flex-end'}>
                <Stack direction={'column'} width={'50%'}>
                    <Typography fontSize={'11px'} pt={'7px'} width={'100%'}>
                        Firma del Cliente: _____________________
                    </Typography>
                    <Typography fontSize={'11px'} pt={'7px'} width={'100%'}>
                        Aclaracion de Firma: _____________________
                    </Typography>
                    <Typography fontSize={'11px'} pt={'7px'} width={'100%'}>
                        Cedula de identidad: _____________________
                    </Typography>
                </Stack>
                <Stack direction={'column'} width={'50%'}>
                    <Typography fontSize={'11px'} pt={'7px'} width={'100%'}>
                        Nombre: _____________________
                    </Typography>
                    <Typography fontSize={'11px'} pt={'7px'} width={'100%'}>
                        Domicilio: _____________________
                    </Typography>
                    <Typography fontSize={'11px'} pt={'7px'} width={'100%'}>
                        Cedula de identidad: _____________________
                    </Typography>
                    <Typography fontSize={'11px'} pt={'7px'} width={'100%'}>
                        Celular: _____________________
                    </Typography>
                    <Typography fontSize={'11px'} pt={'7px'} width={'100%'}>
                        Firma: _____________________
                    </Typography>
                </Stack>
            </Stack>
        </Stack>
    )
}

interface Props {
    data: {
        contract?: SaleContract
        sale?: SaleInfo
        parcel?: ParcelInfo
        budget?: PreSaleInfo
    }[]
    maxHeight?: boolean
    handleClose(): void
}

export function SaleContractNoteDialog({ handleClose, maxHeight, data }: Props) {
    useEffect(() => {
        setTimeout(() => {
            window.scroll(0, 0)
            window.print()
            handleClose()
        }, 100)
    }, [])

    return (
        <Box className="printable">
            <Stack direction={'column'}>
                {data.map(({ contract, sale, parcel, budget }) => (
                    <SaleContractNote
                        contract={contract}
                        sale={sale}
                        parcel={parcel}
                        budget={budget}
                        maxHeight={maxHeight}
                    />
                ))}
            </Stack>
        </Box>
    )
}
